import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Settings, X, Plus, Edit2, Trash2, Wrench, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const SUPER_ADMIN_DISCORD_ID = "227845414378930176"; // Brittany

const ROLES = [
  { value: "admin", label: "Head Administrator", color: "text-purple-400 bg-purple-500/15 border-purple-500/30" },
  { value: "senior_admin", label: "Senior Admin", color: "text-emerald-400 bg-emerald-500/15 border-emerald-500/30" },
  { value: "high_command", label: "High Command", color: "text-[#00E5FF] bg-[#00E5FF]/15 border-[#00E5FF]/30" },
  { value: "ownership", label: "Owners", color: "text-amber-400 bg-amber-500/15 border-amber-500/30" },
];

const PERMISSION_LABELS = {
  staff_dashboard: "Staff Dashboard",
  quota_history: "Quota History",
  reports: "Reports",
  blacklist: "Blacklist",
  staff_dropdown: "Staff Dropdown",
  admin_panel: "Admin Panel",
  announcements: "Announcements",
};

const PermissionBadges = ({ permissions }) => {
  const activePerms = useMemo(
    () => Object.entries(permissions || {}).filter(([, v]) => v),
    [permissions]
  );
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {activePerms.map(([key]) => (
        <span key={key} className="px-1.5 py-0.5 text-[9px] font-mono bg-zinc-800 text-zinc-500 border border-zinc-700">
          {PERMISSION_LABELS[key] || key}
        </span>
      ))}
    </div>
  );
};


const AdminPanel = ({ isOpen, onClose, currentUser, currentDiscordId }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", permissions: {} });
  const [formData, setFormData] = useState({ discord_id: "", name: "", role: "admin" });
  const [repairPreview, setRepairPreview] = useState(null); // dry-run result
  const [repairing, setRepairing] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  const getRoleStyle = (role) => ROLES.find((r) => r.value === role)?.color || "text-zinc-400 bg-zinc-500/15 border-zinc-500/30";
  const getRoleLabel = (role) => ROLES.find((r) => r.value === role)?.label || role;

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin-users`);
      setAdmins(res.data);
    } catch {
      toast.error("Failed to load admin users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen) fetchAdmins(); }, [isOpen]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.discord_id.trim() || !formData.name.trim()) { toast.error("Discord ID and name are required"); return; }
    try {
      await axios.post(`${API}/admin-users`, { ...formData, performed_by: `${currentUser} (ID: ${currentDiscordId})`, requester_discord_id: currentDiscordId });
      toast.success(`${formData.name} added`);
      setShowAddForm(false);
      setFormData({ discord_id: "", name: "", role: "admin" });
      fetchAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to add admin"); }
  };

  const handleUpdate = async (discordId) => {
    try {
      await axios.put(`${API}/admin-users/${discordId}`, { ...editForm, requester_discord_id: currentDiscordId });
      toast.success("Updated");
      setEditingId(null);
      fetchAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (discordId, name) => {
    if (!window.confirm(`Remove ${name} from admin access?`)) return;
    try {
      await axios.delete(`${API}/admin-users/${discordId}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${currentDiscordId})`)}&requester_discord_id=${currentDiscordId}`);
      toast.success(`${name} removed`);
      fetchAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to remove admin"); }
  };

  const runRepairDryRun = async () => {
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/repair-duplicate-punches?dry_run=true`);
      setRepairPreview(res.data);
      if (res.data.staff_affected === 0) toast.success("No duplicate punches found");
      else toast.info(`Found ${res.data.staff_affected} staff affected by double-counting`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan for duplicates");
    } finally {
      setRepairing(false);
    }
  };

  const applyRepair = async () => {
    if (!window.confirm(`Apply repair to ${repairPreview.staff_affected} staff? This will subtract the extra time/reports and cannot be automatically undone.`)) return;
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/repair-duplicate-punches?dry_run=false`);
      toast.success(`Repair complete. Fixed ${res.data.staff_affected} staff, removed ${res.data.duplicate_rows_removed} duplicate log rows.`);
      setRepairPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply repair");
    } finally {
      setRepairing(false);
    }
  };

  const resyncBotCommands = async () => {
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/resync-slash-commands`);
      const guilds = res.data.guilds_synced || [];
      if (guilds.length === 0) {
        toast.warning("Bot is online but not in any guild — check its server membership");
      } else {
        const cmdCount = guilds[0]?.command_count || 0;
        toast.success(`Resynced ${cmdCount} slash commands to ${guilds.length} guild(s). Commands should appear in Discord within seconds.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resync bot commands");
    } finally {
      setResyncing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 15 }}
        className="glass-overlay w-full max-w-4xl max-h-[90vh] overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-panel"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />

        <div className="p-5 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center border border-purple-500/30 bg-purple-500/10">
                <Settings className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <h2 className="font-mono text-sm font-bold text-zinc-200 uppercase tracking-wider">Admin Panel</h2>
                <p className="font-mono text-[9px] text-zinc-600">Manage dashboard access</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 transition-colors" data-testid="close-admin-panel">
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)] scrollbar-thin">
          {!showAddForm && (
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <button onClick={() => setShowAddForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-admin-btn">
                <Plus className="w-3.5 h-3.5" /> ADD ADMIN
              </button>
              {currentDiscordId === SUPER_ADMIN_DISCORD_ID && (
                <button
                  onClick={runRepairDryRun}
                  disabled={repairing}
                  className="btn-secondary flex items-center gap-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                  data-testid="scan-duplicate-punches-btn"
                  title="Scan for and fix any punches / game reports that were logged twice by the bot"
                >
                  <Wrench className="w-3.5 h-3.5" />
                  {repairing ? "SCANNING…" : "REPAIR DOUBLE-COUNTED PUNCHES"}
                </button>
              )}
              {currentDiscordId === SUPER_ADMIN_DISCORD_ID && (
                <button
                  onClick={resyncBotCommands}
                  disabled={resyncing}
                  className="btn-secondary flex items-center gap-2 border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-50"
                  data-testid="resync-slash-commands-btn"
                  title="Force the Discord bot to re-push /punch, /leaderboard, /loa etc. to every guild it's in. Use when slash commands disappear from Discord's autocomplete."
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${resyncing ? "animate-spin" : ""}`} />
                  {resyncing ? "RESYNCING…" : "RESYNC BOT COMMANDS"}
                </button>
              )}
            </div>
          )}

          {repairPreview && (
            <div className="surface-card p-4 mb-5 border border-amber-500/30">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-mono text-xs font-bold text-amber-400 uppercase tracking-wider">Repair Preview (dry run)</h3>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">
                    {repairPreview.duplicate_punch_messages} duplicated punches ·
                    {" "}{repairPreview.duplicate_game_report_messages} duplicated reports ·
                    {" "}{repairPreview.staff_affected} staff affected
                  </p>
                </div>
                <button onClick={() => setRepairPreview(null)} className="p-1 hover:bg-zinc-800" data-testid="close-repair-preview">
                  <X className="w-3.5 h-3.5 text-zinc-500" />
                </button>
              </div>
              {repairPreview.changes.length > 0 && (
                <div className="max-h-56 overflow-y-auto border border-zinc-800 mb-3">
                  <table className="w-full text-[11px] font-mono">
                    <thead className="bg-zinc-900 text-zinc-500 uppercase tracking-wider text-[9px]">
                      <tr>
                        <th className="text-left px-3 py-2">Staff</th>
                        <th className="text-left px-3 py-2">Time</th>
                        <th className="text-left px-3 py-2">Reports</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repairPreview.changes.map((c) => (
                        <tr key={c.staff_id} className="border-t border-zinc-800/60 text-zinc-300">
                          <td className="px-3 py-2">{c.name}</td>
                          <td className="px-3 py-2">
                            {c.minutes_subtracted > 0 ? (
                              <span><span className="text-zinc-500">{c.old_time}</span> → <span className="text-amber-400">{c.new_time}</span> <span className="text-zinc-600">(−{c.minutes_subtracted}m)</span></span>
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            {c.reports_subtracted > 0 ? (
                              <span><span className="text-zinc-500">{c.old_reports}</span> → <span className="text-amber-400">{c.new_reports}</span> <span className="text-zinc-600">(−{c.reports_subtracted})</span></span>
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={applyRepair}
                  disabled={repairing || repairPreview.staff_affected === 0}
                  className="btn-primary disabled:opacity-40"
                  data-testid="apply-repair-btn"
                >
                  {repairing ? "APPLYING…" : `APPLY REPAIR (${repairPreview.staff_affected})`}
                </button>
                <button onClick={() => setRepairPreview(null)} className="btn-secondary" data-testid="cancel-repair-btn">
                  CANCEL
                </button>
              </div>
            </div>
          )}

          {showAddForm && (
            <form onSubmit={handleAdd} className="surface-card p-4 mb-5 border border-purple-500/20">
              <h3 className="font-mono text-xs font-bold text-zinc-200 uppercase tracking-wider mb-3">Add New Admin</h3>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Discord ID *</label>
                  <input type="text" value={formData.discord_id} onChange={(e) => setFormData({ ...formData, discord_id: e.target.value })} placeholder="e.g. 227845414378930176" className="input-field w-full" required data-testid="add-admin-discord-id" />
                </div>
                <div>
                  <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Display Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Name" className="input-field w-full" required data-testid="add-admin-name" />
                </div>
                <div>
                  <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Role</label>
                  <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="select-field w-full" data-testid="add-admin-role">
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary" data-testid="confirm-add-admin">ADD</button>
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-secondary">CANCEL</button>
              </div>
            </form>
          )}

          <div className="surface-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Discord ID</th>
                  <th className="text-left py-3 px-4">Role</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className="text-center py-8 text-zinc-600 font-mono text-xs">LOADING...</td></tr>
                ) : admins.map((admin) => (
                  <tr key={admin.discord_id} className="table-row">
                    {editingId === admin.discord_id ? (
                      <td colSpan="4" className="py-3 px-4">
                        <div className="space-y-3">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block font-mono text-[9px] text-zinc-600 mb-1">Name</label>
                              <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="input-field w-full" />
                            </div>
                            <div>
                              <label className="block font-mono text-[9px] text-zinc-600 mb-1">Role</label>
                              <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="select-field w-full">
                                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                              </select>
                            </div>
                            <div className="flex items-end gap-2">
                              <button onClick={() => handleUpdate(admin.discord_id)} className="btn-primary text-[10px] px-3 py-1.5">SAVE</button>
                              <button onClick={() => setEditingId(null)} className="btn-secondary text-[10px] px-3 py-1.5">CANCEL</button>
                            </div>
                          </div>
                          <div>
                            <label className="block font-mono text-[9px] text-zinc-600 mb-2">Dashboard Access</label>
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox" checked={editForm.permissions?.[key] || false} onChange={(e) => setEditForm({ ...editForm, permissions: { ...editForm.permissions, [key]: e.target.checked } })} className="w-3.5 h-3.5 border-zinc-700 bg-zinc-900 accent-[#00E5FF]" />
                                  <span className="text-xs text-zinc-400">{label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <>
                        <td className="py-3 px-4">
                          <span className="text-zinc-200 text-sm font-medium">{admin.name}</span>
                          <PermissionBadges permissions={admin.permissions} />
                        </td>
                        <td className="py-3 px-4 text-zinc-500 font-mono text-xs">{admin.discord_id}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 text-[10px] font-mono font-bold border ${getRoleStyle(admin.role)}`}>{getRoleLabel(admin.role)}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => { setEditingId(admin.discord_id); setEditForm({ name: admin.name, role: admin.role, permissions: admin.permissions || {} }); }} className="p-1.5 hover:bg-zinc-800 transition-colors" title="Edit">
                              <Edit2 className="w-3.5 h-3.5 text-[#00E5FF]" />
                            </button>
                            <button onClick={() => handleDelete(admin.discord_id, admin.name)} className="p-1.5 hover:bg-zinc-800 transition-colors" title="Remove">
                              <Trash2 className="w-3.5 h-3.5 text-[#FF3366]" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminPanel;
